import { useCallback } from "react";

import { useStore } from "@jti/store/reduxHooks";
import type { RootStoreState } from "@jti/store/types";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { SET_SHIPPING_METHOD_FIXED_CACHE_KEY, useDeliveryOptions } from "@checkout-v2/shared/hooks/useDeliveryOptions";
import {
  SET_SHIPPING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY,
  useSetShippingAddressOnCart,
} from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart";

export const useEnsureCartRequestsAreProcessed = () => {
  const store = useStore<RootStoreState>();
  const { __ } = useTranslations();

  const { setShippingMethodMutationState } = useDeliveryOptions();
  const { setShippingAddressOnCartState } = useSetShippingAddressOnCart();

  const getRequestErrorMessage = useCallback(
    (requestType: "setShippingMethod" | "setShippingAddress"): string => {
      switch (requestType) {
        case "setShippingAddress":
          return __("cart.checkout.error.setShippingAddress");
        case "setShippingMethod":
          return __("cart.checkout.error.setShippingMethod");
      }
    },
    [__]
  );

  const ensureCartRequestsAreProcessed = useCallback(async (): Promise<void> => {
    if (!setShippingMethodMutationState.isLoading && !setShippingAddressOnCartState.isLoading) {
      return Promise.resolve();
    }

    if (setShippingMethodMutationState.isLoading && !setShippingMethodMutationState.requestId) {
      throw new Error(getRequestErrorMessage("setShippingMethod"));
    }

    if (setShippingAddressOnCartState.isLoading && !setShippingAddressOnCartState.requestId) {
      throw new Error(getRequestErrorMessage("setShippingAddress"));
    }

    const getCurrentStates = () => {
      const state = store.getState();

      const setShippingMethodState = state.magentoApi.mutations[SET_SHIPPING_METHOD_FIXED_CACHE_KEY];
      const setShippingAddressState = state.magentoApi.mutations[SET_SHIPPING_ADDRESS_ON_CART_MUTATION_FIXED_CACHE_KEY];

      return {
        setShippingMethodState,
        setShippingAddressState,
      };
    };

    const TIMEOUT_MS = 30_000;

    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error(__("cart.checkout.error.timeout")));
      }, TIMEOUT_MS);

      const check = (unsubscribe: () => void) => {
        const { setShippingMethodState, setShippingAddressState } = getCurrentStates();

        const isStillPending =
          setShippingMethodState?.status === "pending" || setShippingAddressState?.status === "pending";

        if (isStillPending) {
          return;
        }

        clearTimeout(timeoutId);
        unsubscribe();

        if (setShippingMethodState?.status === "rejected") {
          reject(new Error(getRequestErrorMessage("setShippingMethod")));
          return;
        }

        if (setShippingAddressState?.status === "rejected") {
          reject(new Error(getRequestErrorMessage("setShippingAddress")));
          return;
        }

        resolve();
      };

      const unsubscribe = store.subscribe(() => check(unsubscribe));
      check(unsubscribe);
    });
  }, [__, getRequestErrorMessage, setShippingAddressOnCartState, setShippingMethodMutationState, store]);

  return {
    ensureCartRequestsAreProcessed,
  };
};
